import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Loader2,
  Mail,
  RefreshCw,
} from "lucide-react";
import { useAgentAuth } from "../../contexts/AgentAuthContext";
import { getCapturedAgentReturnPath } from "../../utils/agentLoginBootstrap";
import { normalizeAgentReturnPath } from "../../utils/agentReturnPath";
import "../../styles/agent-mobile.css";

const GENERIC_ACKNOWLEDGEMENT =
  "If that email is connected to an active HelloPC agent account, a sign-in link is on its way. It expires in 15 minutes.";

const AgentLogin = () => {
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");
  const successHeadingRef = useRef(null);
  const errorRef = useRef(null);
  const location = useLocation();
  const { agent, requestMagicLink, magicLinkLoading } = useAgentAuth();
  const returnTo = normalizeAgentReturnPath(
    location.state?.from?.pathname || getCapturedAgentReturnPath()
  );

  useEffect(() => {
    if (emailSent) successHeadingRef.current?.focus();
  }, [emailSent]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  if (agent) {
    return <Navigate to={returnTo} replace />;
  }

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    setError("");
    setValidationError(
      value && !validateEmail(value) ? "Enter a valid email address." : ""
    );
  };

  const sendRequest = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !validateEmail(normalizedEmail)) {
      setValidationError("Enter a valid email address.");
      return;
    }

    setError("");
    const result = await requestMagicLink(normalizedEmail, returnTo);
    if (result.success) {
      setEmailSent(true);
      return;
    }

    setError(
      result.error === "Enter a valid email address."
        ? result.error
        : "We couldn’t request a sign-in email right now. Try again."
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await sendRequest();
  };

  const handleUseDifferentEmail = () => {
    setEmailSent(false);
    setError("");
    setEmail("");
    setValidationError("");
  };

  return (
    <main
      id="agent-login-page"
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-8"
    >
      <section id="agent-login-container" className="w-full max-w-md">
        <div
          id="agent-login-card"
          className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 border border-gray-100"
        >
          <header id="agent-login-header" className="text-center mb-8">
            <div
              id="agent-login-icon"
              className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <Mail className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Agent sign in
            </h1>
            <p id="agent-login-instructions" className="text-gray-600 text-sm leading-relaxed">
              Use the email address your coach has on file. We’ll email you a
              secure sign-in link—no password needed.
            </p>
            <p id="agent-login-remembered-device" className="mt-3 text-sm font-medium text-blue-700">
              You’ll usually only do this once on this phone. Using HelloPC within 30 days keeps you signed in.
            </p>
          </header>

          {!emailSent ? (
            <form id="agent-login-form" onSubmit={handleSubmit} noValidate>
              <div id="agent-email-field" className="mb-6">
                <label
                  htmlFor="agent-login-email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email address
                </label>
                <input
                  id="agent-login-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="you@example.com"
                  disabled={magicLinkLoading}
                  aria-invalid={Boolean(validationError)}
                  aria-describedby={
                    validationError
                      ? "agent-login-instructions agent-email-error"
                      : "agent-login-instructions"
                  }
                  className={`w-full min-h-11 px-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                    validationError
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  } ${magicLinkLoading ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
                />
                {validationError && (
                  <p id="agent-email-error" className="mt-2 text-sm text-red-700">
                    {validationError}
                  </p>
                )}
              </div>

              {error && (
                <div
                  id="agent-login-error"
                  ref={errorRef}
                  role="alert"
                  tabIndex="-1"
                  className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4"
                >
                  <p className="flex items-start text-sm text-red-800">
                    <AlertCircle
                      className="w-5 h-5 text-red-500 mr-2 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {error}
                  </p>
                </div>
              )}

              <button
                id="send-agent-login-email"
                type="submit"
                disabled={magicLinkLoading || Boolean(validationError) || !email.trim()}
                className="w-full min-h-11 bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {magicLinkLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                    Sending your email…
                  </>
                ) : (
                  <>
                    Email me a sign-in link
                    <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <section
              id="agent-login-acknowledgement"
              role="status"
              aria-live="polite"
              className="text-center"
            >
              <div
                id="agent-login-success-icon"
                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="w-8 h-8 text-green-600" aria-hidden="true" />
              </div>
              <h2
                ref={successHeadingRef}
                tabIndex="-1"
                className="text-xl font-semibold text-gray-900 mb-3 focus:outline-none"
              >
                Check your email
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {GENERIC_ACKNOWLEDGEMENT}
              </p>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                Check spam or junk. Make sure you used the email your coach has
                on file.
              </p>
              {error && (
                <div
                  id="agent-login-resend-error"
                  ref={errorRef}
                  role="alert"
                  tabIndex="-1"
                  className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-left text-sm text-red-800"
                >
                  {error}
                </div>
              )}
              <div id="agent-login-success-actions" className="mt-6 space-y-3">
                <button
                  id="resend-agent-login-email"
                  type="button"
                  onClick={sendRequest}
                  disabled={magicLinkLoading}
                  className="w-full min-h-11 bg-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center"
                >
                  {magicLinkLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                      Sending your email…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2" aria-hidden="true" />
                      Resend email
                    </>
                  )}
                </button>
                <button
                  id="use-different-agent-email"
                  type="button"
                  onClick={handleUseDifferentEmail}
                  disabled={magicLinkLoading}
                  className="w-full min-h-11 bg-gray-100 text-gray-800 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Use a different email
                </button>
              </div>
            </section>
          )}

          <footer
            id="agent-login-footer"
            className="mt-8 pt-6 border-t border-gray-100 text-center"
          >
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center justify-center text-sm font-semibold text-blue-700 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            >
              Coach sign in
            </Link>
          </footer>
        </div>
      </section>
    </main>
  );
};

export { GENERIC_ACKNOWLEDGEMENT };
export default AgentLogin;
